import { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import QuestionSection from "../../ui/QuestionSection/QuestionSection";
import CommonFooter from "../../CommonFooter/CommonFooter";
import CommonButton from "../../ui/CommonButton/CommonButton";
import classes from "./ResourceRequirements.module.css";
import { useAccreditation } from "../../../hooks/useAccreditation";

const ResourceRequirements = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const [isEditing, setIsEditing] = useState(false);

  const { formData, setFormData, loading, hydrated, fetching, editable, setEditable, handleDone } =
    useAccreditation({
      labId,
      section: "Resource requirements",
      name: "SADCAS F134-A",
      auditorId: user._id,
      isAssessor: user.role === "ASSESSOR",
      isEditing,
      token,
    });

  const handleAnswerChange = (questionId, score, comment, points, content) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: { score, comment, points, content },
    }));
  };

  const questionSets = [
    {
      questions: [
        { content: "General", number: "6.1", hideOptions: true },
        {
          questionId: "Q1",
          content:
            "Does the laboratory have available the personnel, facilities, equipment, reagents, consumables and support services necessary to manage and perform its activities?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
      ],
      questions1: [
        { content: "Agreements with laboratory users", number: "6.7.1", hideOptions: true },
        {
          questionId: "Q2",
          content:
            "Does the laboratory have a procedure to establish and periodically review agreements for providing laboratory activities?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q3",
          content:
            "Does the procedure ensure: a) the requirements are adequately specified; b) the laboratory has the capability and resources to meet the requirements; and c) when applicable, the laboratory advises the user of the specific activities to be performed by referral laboratories and consultants?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q4",
          content:
            "Does the laboratory inform users of any changes to an agreement that can affect examination results?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q5",
          content: "Are records of reviews, including any significant changes retained?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        { content: "Agreements with POCT operators", number: "6.7.2", hideOptions: true },
        {
          questionId: "Q6",
          content:
            "Do service agreements between the laboratory and other parts of the organization using laboratory supported POCT, ensure that respective responsibilities and authorities are specified and communicated?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
      ],
      questions2: [
        { content: "Externally provided products and services - General", number: "6.8.1", hideOptions: true },
        {
          questionId: "Q7",
          content:
            "How does the laboratory ensure that externally provided products and services that affect laboratory activities are suitable when such products and services are: a) intended for incorporation into the laboratory's own activities; b) provided, in part or in full, directly to the user by the laboratory, as received from the external provider; c) used to support the operation of the laboratory?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        { content: "Referral laboratories and consultants", number: "6.8.2", hideOptions: true },
        {
          questionId: "Q8",
          content:
            "Does the laboratory communicate its requirements to referral laboratories and consultants who provide interpretations and advice, for: a) the procedures, examinations, reports and consulting activities to be provided; b) management of critical results; c) any required personnel qualifications and demonstration of competence?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q9",
          content:
            "Unless otherwise specified in the agreement, does the referring laboratory (and not the referral laboratory) maintain the responsibility for ensuring that examination results of the referral laboratory are provided to the person making the request?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        { content: "Review and approval of externally provided products and services", number: "6.8.3", hideOptions: true },
        {
          questionId: "Q10",
          content:
            "Does the laboratory have procedures and retain records for: a) defining, reviewing, and approving the laboratory's requirements for all externally provided products and services?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q11",
          content: "Does the laboratory have procedures for referral of samples?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q12",
          content:
            "Does the laboratory ensure that externally provided products and services conform to the laboratory's established requirements, or where applicable to the relevant requirements of ISO 15189:2022, before they are used or directly provided to the user?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q13",
          content: "Does the laboratory take any actions arising from evaluations of the performance of external providers?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
      ],
      render: (onAnswerChange, answers) => (
        <>
          <QuestionSection
            number="6"
            title="Resource Requirements"
            accreditation
            showPointsField={false}
            questions={questionSets[0].questions}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            number="6.7"
            title="Service Agreements"
            accreditation
            showPointsField={false}
            questions={questionSets[0].questions1}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            number="6.8"
            title="Externally provided products and services"
            accreditation
            showPointsField={false}
            questions={questionSets[0].questions2}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
        </>
      ),
    },
  ];

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet((prev) => prev - 1);
  };

  if (!hydrated || fetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}>
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />
          <h2 className="m-0">Clause 6: Resource Requirements</h2>
        </div>

        {user.role === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => { setIsEditing(true); setEditable(true); }}
            title="Edit"
          />
        )}
      </div>

      {!editable ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS A - Clause 6: Resource Requirements has been submitted.
        </div>
      ) : (
        questionSets[currentSet].render(handleAnswerChange, formData)
      )}

      {editable && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => setEditable(false)}
          currentSet={currentSet}
          questionSets={questionSets}
          doneText={loading ? "Submitting..." : "Update"}
          disabled={loading}
        />
      )}
    </div>
  );
};

export default ResourceRequirements;